// Angular Files
import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot, RedirectCommand, Router, UrlTree
} from '@angular/router';

// Teller Online Files
import {
    PaymentProcessorProvider
} from 'apps/public-portal/src/app/payment-integrations/base/service/payment-processor.provider';

// Teller Online Library Files
import {
    TellerOnlineErrorHandlerService, TellerOnlineSiteMetadataService
} from 'teller-online-libraries/core';
import { TellerOnlineWindowService } from 'teller-online-libraries/shared';

// Service Files
import { AuthService } from './auth.service';
import { CartService } from './cart.service';
import { InboundRedirectService } from './inbound-redirect.service';

/**
 * The query parameters a server-side inbound redirect (HandleRedirect) lands with. The
 * vendor's bill identifiers (SearchId) are not among them: they travel inside the signed
 * returnUrls token, which the completion call hands back to the server.
 */
const REDIRECT_QUERY_PARAMS = ['fromInboundRedirect', 'cartGuid', 'alias', 'returnUrls'];

/**
 * A server-side inbound redirect (HandleRedirect) lands the browser on a fresh Angular
 * boot with the cart guid and alias in the query string. Before the checkout/cart pages
 * construct, sign out (as the legacy /TOL flow does), rebuild the TOL session and adopt
 * that cart; otherwise send the user home with no TOL session left behind.
 *
 * The cart guid is the cart's bearer token: anyone holding it can load and pay the cart.
 * It is taken out of the address bar before the first request leaves this page, so it
 * reaches neither the Referer header the API logs nor browser history, and the navigation
 * ends on the same route with the redirect parameters gone.
 *
 * Attached to a componentless parent route: Angular runs one route's canActivate guards
 * concurrently, but a parent route's guards complete before its children's, which is what
 * lets AccessGuardService read the cart snapshot safely on checkout.
 */
@Injectable({
    providedIn: 'root'
})
export class InboundRedirectHydrationGuardService {
    constructor(
        private router: Router,
        private cartService: CartService,
        private inboundRedirectService: InboundRedirectService,
        private authService: AuthService,
        private siteMetadataService: TellerOnlineSiteMetadataService,
        private paymentProvider: PaymentProcessorProvider,
        private windowService: TellerOnlineWindowService,
        private errorHandlerService: TellerOnlineErrorHandlerService
    ) { }

    async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree | RedirectCommand> {
        const {
            fromInboundRedirect, cartGuid, alias, returnUrls
        } = route.queryParams;
        if (fromInboundRedirect !== 'true') {
            return true;
        }

        if (!cartGuid || !alias) {
            return this.router.parseUrl('/');
        }

        // Decide the destination before doing any work: a re-route is a fresh navigation that
        // runs this guard again, so hydrating first would sign out, hydrate and adopt twice.
        const reroute = this._reconcileCheckoutRoute(route);
        if (reroute) {
            return reroute;
        }

        // Out of the address bar before the context and cart requests below are made: the
        // browser sends the current url as their Referer
        const landingUrl = this._landingUrlWithoutRedirectParams(route);
        this.windowService.replaceState(landingUrl);

        if (this.siteMetadataService.authenticationEnabled) {
            // A guard that rejects fails the whole navigation: on a cold boot that is a blank
            // shell with no error shown and no way home. Treat a failed sign-out like the
            // failures below instead - report it, leave no session behind, go home
            try {
                await this.authService.signOut();
            } catch (e) {
                this.errorHandlerService.handleError(e, false);
                await this.inboundRedirectService.discardRedirectSession();

                return this.router.parseUrl('/');
            }
        }

        // Unknown or inactive alias (the context endpoint's 204), or a failed request. Any
        // keys left by an abandoned earlier session go too, or home would try to resume it.
        // The session's SearchId is not read from the url: the completion call reports the
        // one signed inside the returnUrls token
        if (!await this.inboundRedirectService.hydrateFromRedirect(alias, undefined, returnUrls)) {
            await this.inboundRedirectService.discardRedirectSession();

            return this.router.parseUrl('/');
        }

        if (!await this.cartService.adoptCart(cartGuid)) {
            // The server rejected or lost the cart: leave no half-built TOL session behind
            await this.inboundRedirectService.discardRedirectSession();

            return this.router.parseUrl('/');
        }

        // Checkout reads this instead of the url to recalculate the totals of a cart it did
        // not build itself
        this.inboundRedirectService.cartAdoptedFromRedirect = true;

        // Finish on the clean url. The router writes the navigation's url to the address bar
        // when it completes, which would put the guid back; redirecting to the stripped url
        // (replacing, not pushing, so history holds no entry with the guid) runs this guard
        // once more, where the flag is absent and it passes straight through. That second
        // navigation would also have app.component refresh the cart adoptCart just loaded
        // (the server validated it building it): suppress that, as checkout's own exits do
        return new RedirectCommand(this.router.parseUrl(landingUrl), {
            replaceUrl: true,
            state: { suppressCartRefresh: true }
        });
    }

    /**
     * The backend picked /checkout vs /checkout/redirect from the processor's hosted-page
     * capability; the frontend's static per-processor flag is what the checkout components
     * enforce (checkout bounces to / when they disagree). Re-route rather than bounce.
     * activeCreditProcessor is assigned synchronously in the provider's constructor after the
     * APP_INITIALIZER metadata load, so it is populated on the first navigation.
     */
    private _reconcileCheckoutRoute(route: ActivatedRouteSnapshot): UrlTree | null {
        const path = route.firstChild?.routeConfig?.path;
        const hosted = this.paymentProvider.activeCreditProcessor?.redirect ?? false;
        const query = new URLSearchParams(route.queryParams as Record<string, string>).toString();

        if (path === 'checkout' && hosted) {
            return this.router.parseUrl(`/checkout/redirect?${query}`);
        }

        if (path === 'checkout/redirect' && !hosted) {
            return this.router.parseUrl(`/checkout?${query}`);
        }

        return null;
    }

    /**
     * The child route's actual path (checkout, checkout/redirect) with the
     * redirect parameters removed; any other query parameter stays.
     */
    private _landingUrlWithoutRedirectParams(route: ActivatedRouteSnapshot): string {
        const path = (route.firstChild?.url ?? []).map(segment => segment.path).join('/');
        const query = new URLSearchParams(route.queryParams as Record<string, string>);
        REDIRECT_QUERY_PARAMS.forEach(name => query.delete(name));
        const queryString = query.toString();

        return `/${path}${queryString ? `?${queryString}` : ''}`;
    }
}
